import rollbarLogger from 'config/rollbar';
import { AssetTypeCode } from 'types/models/asset-type';
import { HeaterAssociatedSamplePointIds, SamplePointWithHeaterAddOn } from 'types/models/heater';
import SamplePoint, {
  MergedSamplePoint,
  MergedWeatherStationSamplePoint,
  SamplePointId,
  WeatherChildSensorWithPairedSamplePoint
} from 'types/models/samplePoint';
import { WeatherSamplePoint } from 'types/models/weather-station';
import { AssociatedPowerMeterSamplePoints } from 'utils/associated-sample-points/merge-power-meters';
import { assertIsDefined } from 'utils/TypeScript/assert-is-defined';

/** NOTE: This util will throw if it cannot guarantee the shape of its returned object */
export const getOriginalSamplePointIdsFromMergedPowerMeter = (
  samplePoint: MergedSamplePoint<AssociatedPowerMeterSamplePoints>
): {
  flagsSamplePointId: SamplePointId;
  energySamplePointId: SamplePointId;
  line1VoltageSamplePointId: SamplePointId;
  line1CurrentSamplePointId: SamplePointId;
  line2VoltageSamplePointId?: SamplePointId;
  line2CurrentSamplePointId?: SamplePointId;
  line3VoltageSamplePointId?: SamplePointId;
  line3CurrentSamplePointId?: SamplePointId;
} => {
  assertIsDefined<SamplePoint>(samplePoint._hidden);
  assertIsDefined<SamplePoint>(samplePoint._hidden.energy);
  assertIsDefined<SamplePoint>(samplePoint._hidden.line1Voltage);
  assertIsDefined<SamplePoint>(samplePoint._hidden.line1Current);

  return {
    flagsSamplePointId: samplePoint.id,
    energySamplePointId: samplePoint._hidden.energy.id,
    line1VoltageSamplePointId: samplePoint._hidden.line1Voltage.id,
    line1CurrentSamplePointId: samplePoint._hidden.line1Current.id,
    line2VoltageSamplePointId: samplePoint._hidden.line2Voltage?.id,
    line2CurrentSamplePointId: samplePoint._hidden.line2Current?.id,
    line3VoltageSamplePointId: samplePoint._hidden.line3Voltage?.id,
    line3CurrentSamplePointId: samplePoint._hidden.line3Current?.id
  };
};

export const getAssociatedSamplePointsFromWeatherStationController = (controller: MergedWeatherStationSamplePoint) => {
  if (!controller._hidden) return [];
  return Object.values(controller._hidden) as WeatherSamplePoint[];
};

/**
 * Retrieves the ID of the sample point paired with the given sample point.
 * @summary Certain sample points, such as Temperature and Dew Point in Weather Stations,
 * are paired together for display purposes.
 * @param samplePoint - The sample point to find the paired ID for.
 * @returns The ID of the paired sample point, or undefined if no pair exists.
 */
export const getPairedSamplePointId = (
  samplePoint: MergedSamplePoint<unknown>
): SamplePointId | undefined => {
  switch (samplePoint.assetTypeId) {
    case AssetTypeCode.TEMPERATURE:
    case AssetTypeCode.DEW_POINT:
      return (samplePoint as WeatherChildSensorWithPairedSamplePoint)._hidden?.pairedSamplePointId;
    default:
      return undefined;
  }
};

/**
 * Get sample point id(s) from a merged sample point
 * @summary Most of the sample point only has one id, while like soil sample
 * point has two ids.
 * @param samplePoint - Sample point
 * @returns sample point id array
 */
export default function getOriginalSamplePointIdsFromMergedSamplePoint(
  samplePoint: MergedSamplePoint
): SamplePointId[] {
  switch (samplePoint.assetTypeId) {
    case AssetTypeCode.SOIL:
    case AssetTypeCode.SAFETY_CHECK_IN:
      try {
        assertIsDefined(samplePoint._hidden);
        return [samplePoint.id, samplePoint._hidden.id];
      } catch (error) {
        if (samplePoint.assetTypeId === AssetTypeCode.SAFETY_CHECK_IN) {
          rollbarLogger.warning('Paired SOS sensor not found for check-in sensor', samplePoint);
        }
        return [samplePoint.id];
      }
    case AssetTypeCode.POWER_METER: {
      try {
        const idMap = getOriginalSamplePointIdsFromMergedPowerMeter(
          samplePoint as MergedSamplePoint<AssociatedPowerMeterSamplePoints>
        );
        return Object.values(idMap).filter(Boolean);
      } catch (error) {
        rollbarLogger.warning('[power-meter] Could not retrieve sub-sample point ids', {
          flagsSamplePoint: samplePoint,
          error
        });
        return [samplePoint.id];
      }
    }
    case AssetTypeCode.WEATHER_STATION_CONTROLLER: {
      const associatedSamplePointIds = getAssociatedSamplePointsFromWeatherStationController(
        samplePoint as MergedWeatherStationSamplePoint
      ).map(sp => sp.id);
      return [samplePoint.id, ...associatedSamplePointIds];
    }
    case AssetTypeCode.TEMPERATURE:
    case AssetTypeCode.DEW_POINT: {
      const pairedSp = getPairedSamplePointId(samplePoint);
      if (pairedSp) return [samplePoint.id, pairedSp];
      return [samplePoint.id];
    }
    default: {
      const hiddenHeater: HeaterAssociatedSamplePointIds | undefined =
        (samplePoint as unknown as SamplePointWithHeaterAddOn)._hidden?.heater;
      if (hiddenHeater) {
        const heaterSensorIds = Object.values(hiddenHeater).filter((spId): spId is SamplePointId => !!spId);
        return [samplePoint.id, ...heaterSensorIds];
      }
      return [samplePoint.id];
    }
  }
}
